import React, { useState } from "react";
import Button from "../UI/Button";
import { Select, Input } from "../UI/FormField";
import { Service } from "../../types/service";
import { useTranslation } from "react-i18next";

/**
 * Inline "add service" row backed by the post-completion API endpoints —
 * used on Completed/locked shipments where the normal form flow is
 * unavailable. Picks a service from the catalogue (zone/band for MATRIX),
 * resolves the price, and hands a ready service-snapshot payload to `onSave`.
 */

export interface ServiceAddInlineState {
  /**
   * The catalogue card chosen — the row is picked by CARD, not by code.
   *
   * A company may price one code twice: once on the general rate card, once
   * for a particular client. Keyed on the name, those were two options with
   * the same value, which is one option to a browser — whichever card loaded
   * first won every click and the row recorded a rate nobody had chosen. The
   * id makes them distinguishable, and it is what carries the choice through
   * to invoicing, where the accounting mapping is read off the card.
   */
  id: string;
  name: string;
  type: string;
  description: string;
  tax: string;
  partnerId: string | null;
  price: number;
  qty: number;
  zoneId: string;
  bandId: string;
}

const emptyRow: ServiceAddInlineState = {
  id: "",
  name: "",
  type: "",
  description: "",
  tax: "NO_TAX",
  partnerId: null,
  price: 0,
  qty: 1,
  zoneId: "",
  bandId: "",
};

interface ServiceAddInlineProps {
  services: Service[];
  busy?: boolean;
  onSave: (payload: Record<string, unknown>) => Promise<void>;
  onCancel: () => void;
}

export const ServiceAddInline: React.FC<ServiceAddInlineProps> = ({
  services,
  busy = false,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation("shipments");
  const [row, setRow] = useState<ServiceAddInlineState>({ ...emptyRow });

  const rowService = services.find((s) => s._id === row.id);
  const isMatrix = row.type === "MATRIX";

  const handleSelect = (selectedId: string) => {
    const svc = services.find((s) => s._id === selectedId);
    if (!svc) return;
    setRow((prev) => ({
      ...prev,
      id: svc._id,
      name: svc.name,
      type: svc.type,
      description: svc.description ?? "",
      tax: svc.tax ?? "NO_TAX",
      partnerId: svc.partnerId ?? null,
      price: svc.type === "MATRIX" ? 0 : svc.price ?? 0,
      zoneId: "",
      bandId: "",
    }));
  };

  const resolveMatrixPrice = (zoneId: string, bandId: string) => {
    if (!rowService || !zoneId || !bandId) return 0;
    const cell = rowService.priceMatrix?.find(
      (c) => c.zoneId === zoneId && c.bandId === bandId,
    );
    return cell?.price ?? 0;
  };

  const canSave =
    !busy &&
    !!row.name &&
    (!isMatrix || (!!row.zoneId && !!row.bandId && row.price > 0));

  const handleSave = async () => {
    if (!canSave) return;
    const qty = Math.max(1, Math.floor(Number(row.qty) || 1));
    const price = Number(row.price) || 0;
    const payload: Record<string, unknown> = {
      // Which card this was priced from. Cost rows are priced from the cost
      // catalogue and have no such link, so the cost validator strips it —
      // sending it from the shared control is harmless there.
      catalogServiceId: row.id || null,
      name: row.name,
      type: row.type,
      description: row.description,
      tax: row.tax,
      partnerId: row.partnerId,
      price,
      qty,
      total: Math.round(price * qty * 100) / 100,
    };
    if (isMatrix) {
      const zone = rowService?.zones?.find((z) => z._id === row.zoneId);
      const band = rowService?.bands?.find((b) => b._id === row.bandId);
      payload.matrixContext = {
        zoneName: zone?.name ?? "",
        bandLabel: band?.label ?? "",
        selectedZoneId: row.zoneId,
        selectedBandId: row.bandId,
      };
    }
    await onSave(payload);
  };

  return (
    <div className="mt-2 border border-gray-300 bg-gray-50 rounded-md p-3 space-y-2">
      <div className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">
        {t("services.addServiceCompleted")}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[200px]">
          <label className="block text-[10px] text-gray-500 mb-0.5">
            {t("services.serviceCode")}
          </label>
          <Select
            size="sm"
            value={row.id}
            placeholder={t("services.selectService")}
            // Two cards can share a code, so the label has to say which is
            // which — keying the option on the id makes them two options to
            // the browser; naming the client makes them two to the reader.
            options={services.map((s) => ({
              label: s.partnerId
                ? `${s.name} · ${s.partnerInfo?.name ?? t("services.clientRate")}`
                : s.name,
              value: s._id,
            }))}
            onChange={(e) => handleSelect(e.target.value)}
          />
        </div>
        <div className="min-w-[200px] flex-1">
          <label className="block text-[10px] text-gray-500 mb-0.5">
            {t("services.description")}
          </label>
          <Input
            size="sm"
            value={row.description}
            onChange={(e) => setRow({ ...row, description: e.target.value })}
            placeholder={t("services.enterDescription")}
          />
        </div>
        {isMatrix && (
          <>
            <div className="min-w-[140px]">
              <label className="block text-[10px] text-gray-500 mb-0.5">
                {t("services.zones")}
              </label>
              <select
                value={row.zoneId}
                onChange={(e) => {
                  const zoneId = e.target.value;
                  setRow({
                    ...row,
                    zoneId,
                    price: resolveMatrixPrice(zoneId, row.bandId),
                  });
                }}
                className="w-full h-8 px-2.5 py-1 text-[13px] border border-gray-300 rounded-md"
              >
                <option value="">{t("services.selectZone")}</option>
                {(rowService?.zones ?? []).map((z) => (
                  <option key={z._id} value={z._id}>
                    {z.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-[140px]">
              <label className="block text-[10px] text-gray-500 mb-0.5">
                {t("services.bands")}
              </label>
              <select
                value={row.bandId}
                onChange={(e) => {
                  const bandId = e.target.value;
                  setRow({
                    ...row,
                    bandId,
                    price: resolveMatrixPrice(row.zoneId, bandId),
                  });
                }}
                className="w-full h-8 px-2.5 py-1 text-[13px] border border-gray-300 rounded-md"
              >
                <option value="">{t("services.selectBand")}</option>
                {(rowService?.bands ?? []).map((b) => (
                  <option key={b._id} value={b._id}>
                    {b.label}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
        <div className="w-[90px]">
          <label className="block text-[10px] text-gray-500 mb-0.5">
            {t("services.price")}
          </label>
          {isMatrix ? (
            <span className="block text-[13px] font-medium py-1.5">
              €{Number(row.price).toFixed(2)}
            </span>
          ) : (
            <Input
              size="sm"
              type="text"
              inputMode="decimal"
              className="text-right"
              value={String(row.price)}
              onChange={(e) => {
                const input = e.target.value;
                if (!/^\d{0,5}(\.\d{0,2})?$/.test(input)) return;
                setRow({ ...row, price: parseFloat(input) || 0 });
              }}
            />
          )}
        </div>
        <div className="w-[70px]">
          <label className="block text-[10px] text-gray-500 mb-0.5">
            {t("services.qty")}
          </label>
          <Input
            size="sm"
            type="text"
            inputMode="numeric"
            className="text-right"
            value={String(row.qty)}
            onChange={(e) => {
              const input = e.target.value;
              if (!/^\d{0,5}$/.test(input)) return;
              setRow({ ...row, qty: parseInt(input) || 1 });
            }}
          />
        </div>
        <div className="w-[90px]">
          <label className="block text-[10px] text-gray-500 mb-0.5">
            {t("services.total")}
          </label>
          <span className="block text-[13px] font-medium py-1.5">
            €{((Number(row.price) || 0) * (Number(row.qty) || 1)).toFixed(2)}
          </span>
        </div>
        <div className="flex gap-2 pb-0.5">
          <Button
            type="button"
            variant="primary"
            disabled={!canSave}
            onClick={handleSave}
          >
            {t("services.saveService")}
          </Button>
          <Button type="button" variant="ghost" disabled={busy} onClick={onCancel}>
            {t("services.cancelService")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ServiceAddInline;
